/**
 * Border style constants for EagGrid table elements.
 *
 * Inline styles are required because a global CSS reset
 * (`*, ::before, ::after { border-width: 0px }` — unlayered)
 * overrides Tailwind's `@layer utilities` border classes.
 * Inline styles take precedence over any stylesheet rule.
 *
 * Colors use CSS custom properties from TW v4 so they stay
 * consistent with the design-system palette.
 */
import {CSSProperties} from 'react';

/** Shared border color for row separators and cell dividers. */
const BORDER_COLOR = 'var(--color-gray-200)';

/** 1 px solid line used for all grid separators. */
const BORDER = `1px solid ${BORDER_COLOR}`;

// ── Row (horizontal) borders ────────────────────────────────────

/** Bottom border applied to every data / skeleton row. */
export const ROW_BORDER_BOTTOM = {borderBottom: BORDER} as const;

// ── Cell vertical separator ─────────────────────────────────────

/**
 * Short centered vertical line (50 % cell height) positioned at the
 * right edge of each cell — mirrors the AG Grid column separator look.
 * Used as `style` on an absolutely-positioned `<span>` inside each cell.
 */
export const COLUMN_SEPARATOR_STYLE: CSSProperties = {
  position: 'absolute',
  right: 0,
  top: '25%',
  height: '50%',
  width: 1,
  background: BORDER_COLOR,
  pointerEvents: 'none',
};

/**
 * Full-height blue resize indicator shown on the column being resized.
 * Applied from the header cell all the way through every body cell.
 * Used for columns with a right-side resize handle (left-pinned and unpinned columns).
 */
export const RESIZE_INDICATOR_STYLE: CSSProperties = {
  position: 'absolute',
  right: 0,
  top: 0,
  height: '100%',
  width: 1,
  background: 'var(--color-blue-400)',
  pointerEvents: 'none',
  zIndex: 'var(--z-content-loader)',
};

/**
 * Full-height blue resize indicator for right-pinned columns.
 * Positioned at the LEFT edge because right-pinned columns use a left-side resize handle —
 * the handle is on the left so users can reach it when the column is stuck to the far right.
 */
export const RESIZE_INDICATOR_LEFT_STYLE: CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  width: 1,
  background: 'var(--color-blue-400)',
  pointerEvents: 'none',
  zIndex: 'var(--z-content-loader)',
};

// ── Header ──────────────────────────────────────────────────────

/**
 * Bottom border below every header cell.
 * @deprecated Prefer THEAD_BORDER_BOTTOM applied directly on <thead> instead
 * of repeating this on every <th>. Left here for any remaining individual-cell uses.
 */
export const HEADER_BORDER_BOTTOM = {borderBottom: BORDER} as const;

/**
 * Border applied to the <thead> element.
 *
 * borderTop is intentionally NOT set here: <thead> itself is NOT sticky — only
 * individual <th> cells are sticky. With border-collapse, a borderTop on <thead>
 * causes the browser to reserve space for the border even after it scrolls out of
 * view, creating a visible gap between the sticky header cells and the first data
 * row. The visual separation above the grid is provided by the GridHeader
 * toolbar's own borderBottom.
 *
 * borderBottom is also NOT set: because <thead> is not sticky, its borderBottom
 * disappears during vertical scroll. Instead each <th> carries its own borderBottom
 * via HEADER_CELL_BORDER_BOTTOM so the line stays visible at all scroll positions.
 */
export const THEAD_BORDER: CSSProperties = {};

/**
 * Bottom border applied to every main header cell (<th> in the last header row).
 * Applied as an inline style on each <th> so the border stays visible during
 * vertical scroll (unlike a border on the non-sticky <thead> element).
 */
export const HEADER_CELL_BORDER_BOTTOM: CSSProperties = {
  borderBottom: BORDER,
};

/**
 * Bottom border applied to every group header cell (<th> in GroupHeaderRow).
 * Provides the separator line between the group header row and the main header row
 * that stays visible during vertical scroll since each <th> is individually sticky.
 */
export const GROUP_HEADER_CELL_BORDER_BOTTOM: CSSProperties = {
  borderBottom: BORDER,
};

/**
 * Pixel height of the column-group header row (GroupHeaderRow).
 * Used as the `top` offset for sticky positioning of the main header row
 * cells when the group header row is visible — so the main row sticks
 * directly below the group row instead of overlapping it.
 *
 * Breakdown: text-xs line-height (16px) + py-1 top (4px) + py-1 bottom (4px) = 24px rendered height.
 * We use 25px: 24px rendered content (text-xs 16px + py-1 4px top + 4px bottom)
 * plus 1px for the borderBottom on the group header cells, giving exact total height.
 */
export const GROUP_HEADER_ROW_HEIGHT = 25;

/**
 * Thick orange strip at the top edge of a pinned column header — a visual
 * indicator that the column is pinned, styled like Chrome's tab-group label.
 * Rendered as an absolutely-positioned <span> so it never affects layout.
 */
export const PINNED_INDICATOR_STYLE: CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  height: 3,
  background: 'var(--color-orange-400)',
  pointerEvents: 'none',
};

// ── Pinned column / action column edge lines ─────────────────────
//
// CSS `border` on a <td>/<th> does NOT span the full row height — it is
// clipped by the cell's box model and can appear shorter than the row.
// Instead, we use absolutely-positioned <span> elements (same technique
// as RESIZE_INDICATOR_STYLE and COLUMN_SEPARATOR_STYLE) so the line
// always covers 100 % of the cell height.

/**
 * Full-height vertical line at the RIGHT edge of every left-pinned cell.
 * Renders as an absolutely-positioned <span> inside the cell.
 */
export const PINNED_LEFT_EDGE_LINE_STYLE: CSSProperties = {
  position: 'absolute',
  right: 0,
  top: 0,
  height: '100%',
  width: 1,
  background: BORDER_COLOR,
  pointerEvents: 'none',
};

/**
 * Full-height vertical line at the LEFT edge of every right-pinned cell
 * and the actions column.
 * Renders as an absolutely-positioned <span> inside the cell.
 */
export const PINNED_RIGHT_EDGE_LINE_STYLE: CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  width: 1,
  background: BORDER_COLOR,
  pointerEvents: 'none',
};

// ── Drag overlay ────────────────────────────────────────────────

/** Outer border for the full-column drag overlay container. */
export const OVERLAY_BORDER = {border: BORDER} as const;

// ── Section separators ──────────────────────────────────────────

/**
 * Bottom border for grid sections (toolbar, header bar, bulk-actions bar).
 * Visually separates sections stacked above the table.
 */
export const SECTION_BORDER_BOTTOM: CSSProperties = {
  borderBottom: BORDER,
};
